import { ActionType, AnalysisType, AppMessageActionModel, CameraActionModel,
         Comparator, DeviceInfo, EmailActionModel, Event, EventAction, EventActionInfo, EventInfo, EventType,
         IrrigationActionModel, LogicOperator, MotionCondition, MotionConditionInfo,
         NotificationSectionOptionsModel,
         SensorCondition, SensorConditionInfo, SensorEventResultInfo, SensorGroup,
         SensorGroupName, TaskActionModel, TimerCondition, TimerConditionInfo,
         TimerEventResultInfo, TimerEventType, TimingInterval,
         FloodIrrigationActionModel} from '@models';
import { ConditionResult } from '@models/Event/TaskResult.model';
import { UserAccessType } from '@models/User/UserAccessType.model';
import { CommonShared } from 'app/shared/classes/common';
import moment from 'moment';
import { Action } from 'rxjs/internal/scheduler/Action';


export interface TriggerMapping {
    mapEventInfos2Event(eventInfos: EventInfo[]): Event[];
    mapEventInfo2Event(eventInfo: EventInfo): Event;
    mapEvent2EventInfo(event: Event): EventInfo;
    mapSensorEventInfos2SensorEvents(sensorEventInfos: SensorConditionInfo[]): SensorCondition[];
    mapSensorEvents2SensorEventInfos(sensorEvents: SensorCondition[]): SensorConditionInfo[];
    mapTimerEventInfo2sTimerEvents(eventInfo: EventInfo): TimerCondition[];
    mapTimerEvents2TimerEventInfos(event: Event): TimerConditionInfo[];
    mapMotionEventInfo2sMotionEvents(eventInfo: EventInfo): MotionCondition[];
    mapMotionEvents2TMotionEventInfos(event: Event): MotionConditionInfo[];
    mapEventActionInfos2EventActions(eventActionInfos: EventActionInfo[]): EventAction[];
    mapEventActions2EventActionInfos(eventActions: EventAction[]): EventActionInfo[];
    convertDaysOfWeekString2BoolArray(daysOfWeek: string): boolean[];
    convertDaysOfWeekBoolArray2String(daysOfWeek: boolean[]): string;
    parseUCaseEventInfoObj2LCaseEventInfo(eventInfoObj: string): EventInfo;
    updateEventsWithEvent(events: Event[], event: Event);
}
export { TriggerMapping as EventMapper };


export class TriggerMapping {

  //private common = new CommonShared();
  public devices: DeviceInfo[] = [];
  
  constructor() {}

  public mapEventInfos2Events(eventInfos: EventInfo[]): Event[] {
    const newEvents: Event[] = [];
    eventInfos.forEach(e => {
      const newEvent = this.mapEventInfo2Event(e);
      newEvents.push(newEvent);
    });
    return newEvents;
  }

  public mapEventInfo2Event(eventInfo: EventInfo): Event {
    const newEvent: Event = {
      id: eventInfo.id,
      name: eventInfo.name,
      description: eventInfo.description,
      userName: eventInfo.userName,
      userGroup: eventInfo.userGroup,
      userGroupID: eventInfo.userGroupId,
      userAccess: UserAccessType.Read,
      eventType: this.getEventTypeAtIndex(eventInfo.eventType),
      sensorConditions: (eventInfo.sensorConditions != null ? this.mapSensorEventInfos2SensorEvents(eventInfo.sensorConditions) : []),
      timerConditions: (eventInfo.timerConditions != null ? this.mapTimerEventInfos2TimerEvents(eventInfo) : []),
      motionConditions: (eventInfo.motionConditions != null ? this.mapMotionEventInfos2MotionEvents(eventInfo) : []),
      modelConditions: eventInfo.modelConditions,
      eventActions: this.mapEventActionInfos2EventActions(eventInfo.eventActions),
      isDisabled: eventInfo.isDisabled,
      createdDate: moment.utc(eventInfo.createdDate).local().toDate(),
      modifiedDate: moment.utc(eventInfo.modifiedDate).local().toDate(),
      ignoreUntilDate: moment.utc(eventInfo.ignoreUntilDate).local().toDate(),
      evaluateStartTime: (eventInfo.evaluateStartTime != null ? this.time2Local(eventInfo.evaluateStartTime) : '00:00'),
      evaluateEndTime: (eventInfo.evaluateEndTime != null ? this.time2Local(eventInfo.evaluateEndTime) : '23:59'),
      evaluateDaysOfWeek: (eventInfo.evaluateDaysOfWeek != null ? this.convertDaysOfWeekString2BoolArray(eventInfo.evaluateDaysOfWeek) : [false, false, false, false, false, false, false]),
      checked: false,
      overallActionStatus: { state: 0, duration: 0 },
      miniChartData: { sensors: [], sensorReadings: [], sensorReadingTypes: [] },
      useActionQueue: (eventInfo.actionQueueId != null && eventInfo.actionQueueId > 0),
      actionQueueId: (eventInfo.actionQueueId != null ? eventInfo.actionQueueId : 0)
    };

    newEvent.tooltips = {};
    newEvent.itemColor = {};

    newEvent.evaluateDaysOfWeek = this.convertUTCDaysOfWeek2Local(eventInfo.evaluateStartTime, newEvent.evaluateStartTime, newEvent.evaluateDaysOfWeek);
    return newEvent;
  }

  public mapEvent2EventInfo(event: Event): EventInfo {
    const newEventInfo: EventInfo = {
      id: event.id,
      name: event.name,
      description: event.description,
      userName: event.userName,
      userGroup: event.userGroup,
      userGroupId: event.userGroupID,
      eventType: Object.values(EventType).indexOf(event.eventType),
      sensorConditions: this.mapSensorEvents2SensorEventInfos(event.sensorConditions),
      timerConditions: this.mapTimerEvents2TimerEventInfos(event),
      motionConditions: this.mapMotionEvents2MotionEventInfos(event),
      modelConditions: event.modelConditions,
      eventActions: this.mapEventActions2EventActionInfos(event.eventActions),
      isDisabled: event.isDisabled,
      createdDate: event.createdDate,
      modifiedDate: event.modifiedDate,
      ignoreUntilDate: event.ignoreUntilDate,
      evaluateStartTime: this.time2UTC(event.evaluateStartTime),
      evaluateEndTime: this.time2UTC(event.evaluateEndTime),
      evaluateDaysOfWeek: this.convertDaysOfWeekBoolArray2String(event.evaluateDaysOfWeek),
      actionQueueId: event.actionQueueId
    };
    newEventInfo.evaluateDaysOfWeek = this.convertLocalDaysOfWeek2UTC(event.evaluateStartTime, newEventInfo.evaluateStartTime, newEventInfo.evaluateDaysOfWeek);
    return newEventInfo;
  }


  public mapSensorEventInfos2SensorEvents(sensorEventInfos: SensorConditionInfo[]): SensorCondition[] {
    const newSensorEvents: SensorCondition[] = [];

    sensorEventInfos.forEach(s => {
      const newSensorEvent: SensorCondition = {
        id: s.id,
        eventID: s.eventID,
        sensorGroupID: s.sensorGroupID,
        sensorGroup: undefined, // { id: s.sensorGroupID, name: '' },
        sensorReadingType: { id: s.sensorReadingTypeID, type: '' },
        numberOfSamples: s.numberOfSamples,
        lastXMinutes: s.lastXMinutes,
        analysisType: this.getAnalysisTypeAtIndex(s.analysisType),
        comparator: this.getComparatorAtIndex(s.comparator),
        threshold: s.threshold,
        logicOperator: this.getLogicOperatorAtIndex(s.logicOperator),
        order: s.order
      };
      newSensorEvents.push(newSensorEvent);
    });
    this.sortSensorEventsByOrder(newSensorEvents);
    return newSensorEvents;
  }


  public mapSensorEvents2SensorEventInfos(sensorEvents: SensorCondition[]): SensorConditionInfo[] {
    const newSensorEventInfos: SensorConditionInfo[] = [];

    sensorEvents.forEach(s => {
      if (s.numberOfSamples == undefined || s.numberOfSamples < 0) {
        s.numberOfSamples = 0;
      }
      if (s.lastXMinutes == undefined || s.lastXMinutes < 0) {
        s.lastXMinutes = 0;
      }

      const newSensorEventInfo: SensorConditionInfo = {
        id: s.id,
        eventID: s.eventID,
        sensorGroupID: s.sensorGroup.id,
        sensorReadingTypeID: s.sensorReadingType.id,
        numberOfSamples: s.numberOfSamples,
        lastXMinutes: s.lastXMinutes,
        analysisType: Object.values(AnalysisType).indexOf(s.analysisType),
        comparator: Object.values(Comparator).indexOf(s.comparator),
        threshold: s.threshold,
        logicOperator: Object.values(LogicOperator).indexOf(s.logicOperator),
        order: s.order
      };
      newSensorEventInfos.push(newSensorEventInfo);
    });
    return newSensorEventInfos;
  }


  public mapTimerEventInfos2TimerEvents(eventInfo: EventInfo): TimerCondition[] {
    const timerEventInfos = eventInfo.timerConditions;
    const newTimerEvents: TimerCondition[] = [];

    timerEventInfos.forEach(t => {
      const newTimerEvent: TimerCondition = {
        id: t.id,
        eventID: t.eventID,
        type: this.getTimerEventTypeAtIndex(t.type),
        timingSingle: moment.utc(t.timingSingle).local().format('HH:mm'),
        timingRepeat: this.getTimingIntervalAtIndex(t.timingRepeat),
        logicOperator: this.getLogicOperatorAtIndex(t.logicOperator),
        daysOfWeek: this.convertDaysOfWeekString2BoolArray(t.daysOfWeek),
        order: t.order
      };

      if (newTimerEvent.type === TimerEventType.Single) {
        newTimerEvent.daysOfWeek = this.convertUTCDaysOfWeek2Local(t.timingSingle, newTimerEvent.timingSingle, newTimerEvent.daysOfWeek);
      } else {
        newTimerEvent.daysOfWeek = this.convertUTCDaysOfWeek2Local(eventInfo.evaluateStartTime, this.time2Local(eventInfo.evaluateStartTime), newTimerEvent.daysOfWeek);
      }

      newTimerEvents.push(newTimerEvent);
    });

    this.sortTimerEventsByOrder(newTimerEvents);
    return newTimerEvents;
  }


  public mapTimerEvents2TimerEventInfos(event: Event): TimerConditionInfo[] {
    const timerEvents = event.timerConditions;
    const newTimerEventInfos: TimerConditionInfo[] = [];

    timerEvents.forEach(t => {
      const newTimerEventInfo: TimerConditionInfo = {
        id: t.id,
        eventID: t.eventID,
        type: Object.values(TimerEventType).indexOf(t.type),
        timingSingle: moment(t.timingSingle, 'HH:mm').toDate(),
        timingRepeat: Object.values(TimingInterval).indexOf(t.timingRepeat),
        logicOperator: Object.values(LogicOperator).indexOf(t.logicOperator),
        daysOfWeek: this.convertDaysOfWeekBoolArray2String(t.daysOfWeek),
        order: t.order
      };

      if (t.type === TimerEventType.Single) {
        newTimerEventInfo.daysOfWeek = this.convertLocalDaysOfWeek2UTC(t.timingSingle, newTimerEventInfo.timingSingle, newTimerEventInfo.daysOfWeek);
      } else {
        newTimerEventInfo.daysOfWeek = this.convertLocalDaysOfWeek2UTC(event.evaluateStartTime, this.time2UTC(event.evaluateStartTime), newTimerEventInfo.daysOfWeek);
      }

      newTimerEventInfos.push(newTimerEventInfo);
    });

    return newTimerEventInfos;
  }


  public mapMotionEventInfos2MotionEvents(eventInfo: EventInfo): MotionCondition[] {
    const motionEventInfos = eventInfo.motionConditions;
    const newMotionEvents: MotionCondition[] = [];

    motionEventInfos.forEach(t => {
      const newMotionEvent: MotionCondition = {
        id: t.id,
        eventID: t.eventID,
        logicOperator: this.getLogicOperatorAtIndex(t.logicOperator),
        order: t.order,
        sensitivity: t.sensitivity,
        detectionArea: t.detectionArea,
        postMotionSleepTime: t.postMotionSleepTime,
        preMotionCaptureTime: t.preMotionCaptureTime,
        deviceSerial: t.deviceSerial,
        deviceName: this.getDeviceNameFromSerial(this.devices, t.deviceSerial)
      };

      newMotionEvents.push(newMotionEvent);
    });

    this.sortMotionEventsByOrder(newMotionEvents);
    return newMotionEvents;
  }


  public mapMotionEvents2MotionEventInfos(event: Event): MotionConditionInfo[] {
    const motionEvents = event.motionConditions;
    const newMotionEventInfos: MotionConditionInfo[] = [];

    motionEvents.forEach(t => {
      const newMotionEventInfo: MotionConditionInfo = {
        id: t.id,
        deviceSerial: t.deviceSerial,
        eventID: t.eventID,
        logicOperator: Object.values(LogicOperator).indexOf(t.logicOperator),
        order: t.order,
        sensitivity: t.sensitivity,
        detectionArea: t.detectionArea,
        postMotionSleepTime: t.postMotionSleepTime,
        preMotionCaptureTime: t.preMotionCaptureTime
      };

      newMotionEventInfos.push(newMotionEventInfo);
    });

    return newMotionEventInfos;
  }


  mapEventActionInfos2EventActions(eventActionInfos: EventActionInfo[]): EventAction[] {
    const newEventActions: EventAction[] = [];

    eventActionInfos.forEach(x => {
      const newEventAction: EventAction = {
        id: x.id,
        eventID: x.eventID,
        type: this.getActionTypeAtIndex(x.type),
        destination: '',
        deviceSerial: '',
        deviceName: '',
        taskAction: '',
        zone: 20,
        scheduleName: '',
        scheduleID: -1,
        onError: x.onError,
        mediaResolution: '',
        mediaDuration: 0,
        mediaDelay: 0,
        cameraId: 0,
        cameraName: '',
        cameraModel: '',
        cameraPresets: '',
        mediaActionOptions: [],
        sourceZone: 21,
        fillZone: 22,
        fillScheduleName: '',
        fillScheduleId: -1,
        drainZone: 23,
        drainScheduleName: '',
        drainScheduleId: -1,
        irrigationActionOptions: undefined,
        notificationSectionOptions: undefined
      };

      if (x.destination != undefined && x.destination != '') {
        this.updateEventActionWithDestinationInfo(newEventAction, x.destination);
      }
      newEventActions.push(newEventAction);
    });
    return newEventActions;
  }


  mapEventActions2EventActionInfos(eventActions: EventAction[]): EventActionInfo[] {
    const newEventActionInfos: EventActionInfo[] = [];

    eventActions.forEach(a => {
      const newEventActionInfo: EventActionInfo = {
        id: a.id,
        eventID: a.eventID,
        type: Object.values(ActionType).indexOf(a.type),
        destination: this.mapEventActionProperties2Destination(a),
        onError: a.onError
      };
      newEventActionInfos.push(newEventActionInfo);
    });
    return newEventActionInfos;
  }


  mapEventActionProperties2Destination(eventAction: EventAction): string {
    if (eventAction.type === ActionType.Task) {
      const newTaskAction: TaskActionModel = {} as TaskActionModel;
      newTaskAction.SerialNumber = eventAction.deviceSerial;
      newTaskAction.TaskAction = eventAction.taskAction;
      return JSON.stringify(newTaskAction);
    }
    else if (eventAction.type === ActionType.Irrigation) {
      const newIrrigationAction: IrrigationActionModel = {} as IrrigationActionModel;
      newIrrigationAction.SerialNumber = eventAction.deviceSerial;
      newIrrigationAction.Zone = Number(eventAction.zone);
      newIrrigationAction.WaterSchedule = eventAction.schedule.name;  // eventAction.scheduleName; // 
      newIrrigationAction.WaterScheduleID = eventAction.schedule.id;  // eventAction.scheduleID; // 
      newIrrigationAction.ActionOptions = eventAction.irrigationActionOptions;
      return JSON.stringify(newIrrigationAction);
    }
    else if (eventAction.type === ActionType.FloodIrrigation) {
      const newFloodIrrigationWithSrc: FloodIrrigationActionModel = {} as FloodIrrigationActionModel;
      newFloodIrrigationWithSrc.SerialNumber = eventAction.deviceSerial;
      newFloodIrrigationWithSrc.SourceZone = Number(eventAction.sourceZone);
      newFloodIrrigationWithSrc.FillZone = Number(eventAction.fillZone);
      newFloodIrrigationWithSrc.FillSchedule = eventAction.fillSchedule.name;
      newFloodIrrigationWithSrc.FillScheduleId = eventAction.fillSchedule.id;
      newFloodIrrigationWithSrc.DrainZone = Number(eventAction.drainZone);
      newFloodIrrigationWithSrc.DrainSchedule = eventAction.drainSchedule.name;
      newFloodIrrigationWithSrc.DrainScheduleId = eventAction.drainSchedule.id;
      newFloodIrrigationWithSrc.ActionOptions = eventAction.irrigationActionOptions;
      return JSON.stringify(newFloodIrrigationWithSrc);
    }
    else if (eventAction.type === ActionType.Camera) {
      const newCaptureAction: CameraActionModel = {} as CameraActionModel;
      newCaptureAction.SerialNumber = eventAction.deviceSerial;
      newCaptureAction.CameraId = eventAction.cameraId;
      newCaptureAction.CameraPresets = eventAction.cameraPresets;
      newCaptureAction.ActionOptions = eventAction.mediaActionOptions;
      return JSON.stringify(newCaptureAction);
    }
    else if ((eventAction.type === ActionType.Email || eventAction.type === ActionType.AppMessage) &&
             (eventAction.destination == undefined || eventAction.destination == '')) {
      return eventAction.destination;      // Nothing to save the options alongside
    }
    else if (eventAction.type === ActionType.Email) {
      const options = this.notificationSectionOptionsOrDefault(eventAction);
      const newEmailAction: EmailActionModel = {} as EmailActionModel;
      newEmailAction.EmailAddress = eventAction.destination;
      newEmailAction.IncludeMatchedReadings = options.IncludeMatchedReadings;
      newEmailAction.IncludeAllReadings = options.IncludeAllReadings;
      return JSON.stringify(newEmailAction);
    }
    else if (eventAction.type === ActionType.AppMessage) {
      const options = this.notificationSectionOptionsOrDefault(eventAction);
      const newAppMessageAction: AppMessageActionModel = {} as AppMessageActionModel;
      newAppMessageAction.Message = eventAction.destination;
      newAppMessageAction.IncludeMatchedReadings = options.IncludeMatchedReadings;
      newAppMessageAction.IncludeAllReadings = options.IncludeAllReadings;
      return JSON.stringify(newAppMessageAction);
    }
    else {
      return eventAction.destination;
    }
  }


  // What an action that has never had its options saved gets: the readings that met the
  // condition only, which is the notification body KIG sent before the two lists existed.
  private notificationSectionOptionsOrDefault(eventAction: EventAction): NotificationSectionOptionsModel {
    if (eventAction.notificationSectionOptions != undefined) {
      return eventAction.notificationSectionOptions;
    }
    return { IncludeMatchedReadings: true, IncludeAllReadings: false };
  }


  updateEventActionWithDestinationInfo(eventAction: EventAction, destinationInfo: string) {
    if (eventAction.type == ActionType.Task) {
      const newAction = JSON.parse(destinationInfo);
      eventAction.deviceSerial = (newAction.hasOwnProperty('serialNumber')) ? newAction.serialNumber : newAction.SerialNumber;
      eventAction.taskAction = (newAction.hasOwnProperty('taskAction')) ? newAction.taskAction : newAction.TaskAction;
    }
    else if (eventAction.type == ActionType.Irrigation) {
      const newIrrigationAction = JSON.parse(destinationInfo);
      eventAction.deviceSerial = (newIrrigationAction.hasOwnProperty('serialNumber')) ? newIrrigationAction.serialNumber : newIrrigationAction.SerialNumber;
      eventAction.zone = (newIrrigationAction.hasOwnProperty('zone')) ? newIrrigationAction.zone : newIrrigationAction.Zone;
      eventAction.scheduleName = (newIrrigationAction.hasOwnProperty('waterSchedule')) ? newIrrigationAction.waterSchedule : newIrrigationAction.WaterSchedule;
      eventAction.scheduleID = (newIrrigationAction.hasOwnProperty('waterScheduleID')) ? newIrrigationAction.waterScheduleID : newIrrigationAction.WaterScheduleID;
      eventAction.irrigationActionOptions = (newIrrigationAction.hasOwnProperty('actionOptions')) ? newIrrigationAction.actionOptions : newIrrigationAction.ActionOptions;
    }
    else if (eventAction.type == ActionType.FloodIrrigation) {
      const newFloodIrrigation = JSON.parse(destinationInfo);
      eventAction.deviceSerial = (newFloodIrrigation.hasOwnProperty('serialNumber')) ? newFloodIrrigation.serialNumber : newFloodIrrigation.SerialNumber;
      eventAction.sourceZone = (newFloodIrrigation.hasOwnProperty('sourceZone')) ? newFloodIrrigation.sourceZone : newFloodIrrigation.SourceZone;
      eventAction.fillZone = (newFloodIrrigation.hasOwnProperty('fillZone')) ? newFloodIrrigation.fillZone : newFloodIrrigation.FillZone;
      eventAction.fillScheduleName = (newFloodIrrigation.hasOwnProperty('fillSchedule')) ? newFloodIrrigation.fillSchedule : newFloodIrrigation.FillSchedule;
      eventAction.fillScheduleId = (newFloodIrrigation.hasOwnProperty('fillScheduleId')) ? newFloodIrrigation.fillScheduleId : newFloodIrrigation.FillScheduleId;
      eventAction.drainZone = (newFloodIrrigation.hasOwnProperty('drainZone')) ? newFloodIrrigation.drainZone : newFloodIrrigation.DrainZone;
      eventAction.drainScheduleName = (newFloodIrrigation.hasOwnProperty('drainSchedule')) ? newFloodIrrigation.drainSchedule : newFloodIrrigation.DrainSchedule;
      eventAction.drainScheduleId = (newFloodIrrigation.hasOwnProperty('drainScheduleId')) ? newFloodIrrigation.drainScheduleId : newFloodIrrigation.DrainScheduleId;
      eventAction.irrigationActionOptions = (newFloodIrrigation.hasOwnProperty('actionOptions')) ? newFloodIrrigation.actionOptions : newFloodIrrigation.ActionOptions;
    }
    else if (eventAction.type == ActionType.Camera) {
      const newAction = JSON.parse(destinationInfo);
      eventAction.deviceSerial = (newAction.hasOwnProperty('serialNumber')) ? newAction.serialNumber : newAction.SerialNumber;
      eventAction.cameraId = (newAction.hasOwnProperty('cameraId')) ? newAction.resolution : newAction.CameraId;
      eventAction.cameraPresets = (newAction.hasOwnProperty('cameraPresets')) ? newAction.cameraPresets : newAction.CameraPresets;
      eventAction.mediaActionOptions = (newAction.hasOwnProperty('actionOptions')) ? newAction.actionOptions : newAction.ActionOptions;
    }
    else if (eventAction.type == ActionType.Email || eventAction.type == ActionType.AppMessage) {
      // An address or a message saved before the options existed is stored on its own, so both
      // shapes have to be read. Either way destination ends up holding just the address or the
      // message, which is what the rest of the screen shows and edits.
      const newAction = this.parseNotificationDestination(destinationInfo);

      if (newAction == undefined) {
        eventAction.destination = destinationInfo;
        eventAction.notificationSectionOptions = undefined;
      }
      else if (eventAction.type == ActionType.Email) {
        eventAction.destination = (newAction.hasOwnProperty('emailAddress')) ? newAction.emailAddress : newAction.EmailAddress;
        eventAction.notificationSectionOptions = this.readNotificationSectionOptions(newAction);
      }
      else {
        eventAction.destination = (newAction.hasOwnProperty('message')) ? newAction.message : newAction.Message;
        eventAction.notificationSectionOptions = this.readNotificationSectionOptions(newAction);
      }
    }
    else {
      eventAction.destination = destinationInfo;
    }
  }


  // Returns the parsed destination, or undefined when the stored value is the plain address or
  // message every one of these actions held before the options were added.
  private parseNotificationDestination(destinationInfo: string): any {
    if (destinationInfo == undefined || !destinationInfo.trim().startsWith('{')) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(destinationInfo);
      const hasTarget = parsed != undefined && (parsed.emailAddress != undefined || parsed.EmailAddress != undefined ||
                                                parsed.message != undefined || parsed.Message != undefined);
      return (hasTarget) ? parsed : undefined;
    }
    catch {
      return undefined;      // Not the json expected here, so the value is kept as it is
    }
  }


  private readNotificationSectionOptions(parsedDestination: any): NotificationSectionOptionsModel {
    return {
      IncludeMatchedReadings: (parsedDestination.hasOwnProperty('includeMatchedReadings')) ? parsedDestination.includeMatchedReadings : parsedDestination.IncludeMatchedReadings,
      IncludeAllReadings: (parsedDestination.hasOwnProperty('includeAllReadings')) ? parsedDestination.includeAllReadings : parsedDestination.IncludeAllReadings
    };
  }


  public parseUCaseEventInfoObj2LCaseEventInfo(eventInfoObj: string): EventInfo {
    var temp = JSON.parse(eventInfoObj);

    var eventInfo: EventInfo = {
      id: temp.ID,
      name: temp.Name,
      description: temp.Description,
      userName: temp.UserName,
      userGroup: temp.UserGroup,
      userGroupId: temp.UserGroupId,
      eventType: temp.EventType,
      sensorConditions: temp.SensorConditions,
      timerConditions: temp.TimerConditions,
      modelConditions: temp.ModelConditions,
      motionConditions: temp.MotionConditions,
      eventActions: temp.EventActions,
      isDisabled: temp.IsDisabled,
      createdDate: temp.CreatedDate,
      modifiedDate: temp.ModifiedDate,
      ignoreUntilDate: temp.ignoreUntilDate,
      evaluateStartTime: temp.EvaluateStartTime,
      evaluateEndTime: temp.EvaluateEndTime,
      evaluateDaysOfWeek: temp.EvaluateDaysOfWeek,
      actionQueueId: temp.ActionQueueId
    }

    for (var index = 0; index < eventInfo.sensorConditions.length; index++) {
      var s = temp.SensorConditions[index];
      eventInfo.sensorConditions[index] = {
        id: s.ID,
        eventID: s.EventID,
        sensorGroupID: s.SensorGroupID,
        sensorReadingTypeID: s.SensorReadingTypeID,
        numberOfSamples: s.NumberOfSamples,
        lastXMinutes: s.LastXMinutes,
        analysisType: s.AnalysisType,
        comparator: s.Comparator,
        threshold: s.Threshold,
        logicOperator: s.LogicOperator,
        order: s.Order
      }
    }

    for (var index = 0; index < eventInfo.timerConditions.length; index++) {
      var t = temp.TimerConditions[index];
      eventInfo.timerConditions[index] = {
        id: t.ID,
        eventID: t.EventID,
        type: t.Type,
        timingSingle: t.TimingSingle,
        timingRepeat: t.TimingRepeat,
        logicOperator: t.LogicOperator,
        daysOfWeek: t.DaysOfWeek,
        order: t.Order
      }
    }

    for (var index = 0; index < eventInfo.motionConditions.length; index++) {
      var t = temp.MotionConditions[index];
      eventInfo.motionConditions[index] = {
        id: t.ID,
        eventID: t.EventID,
        logicOperator: t.LogicOperator,
        order: t.Order,  
        sensitivity: t.Sensitivity,
        detectionArea: t.DetectionArea,
        postMotionSleepTime: t.PostMotionSleepTime,
        preMotionCaptureTime: t.PreMotionCaptureTime,
        deviceSerial: t.DeviceSerial
      }
    }

    for (var index = 0; index < eventInfo.eventActions.length; index++) {
      var a = temp.EventActions[index];
      eventInfo.eventActions[index] = {
        id: a.ID,
        eventID: a.EventID,
        type: a.Type,
        destination: a.Destination,
        onError: a.OnError
      }
    }

    return eventInfo;
  }


  public updateEventsWithEvent(events: Event[], updatedEvent: Event) {
    var matchedEventIndex = events.findIndex(e => e.id === updatedEvent.id);
    if (matchedEventIndex < 0) return;

    var originalEvent = events[matchedEventIndex];

    originalEvent.id = updatedEvent.id;
    originalEvent.name = updatedEvent.name;
    originalEvent.description = updatedEvent.description;
    originalEvent.userName = updatedEvent.userName;
    originalEvent.userGroup = updatedEvent.userGroup;
    originalEvent.userGroupID = updatedEvent.userGroupID;
    originalEvent.userAccess = updatedEvent.userAccess;
    originalEvent.eventType = updatedEvent.eventType;
    originalEvent.isDisabled = updatedEvent.isDisabled;
    originalEvent.createdDate = updatedEvent.createdDate;
    originalEvent.modifiedDate = updatedEvent.modifiedDate;
    originalEvent.evaluateStartTime = updatedEvent.evaluateStartTime;
    originalEvent.evaluateEndTime = updatedEvent.evaluateEndTime;
    originalEvent.evaluateDaysOfWeek = updatedEvent.evaluateDaysOfWeek;
    originalEvent.isDisabledColor = updatedEvent.isDisabledColor;

    if (updatedEvent.sensorConditions != undefined) {
      var sensorConditions: SensorCondition[] = [];
      for (var index = 0; index < updatedEvent.sensorConditions.length; index++) {
        var s = updatedEvent.sensorConditions[index];
        const newSensorCondition: SensorCondition = {
          id: s.id,
          eventID: s.eventID,
          sensorGroupID: s.sensorGroupID,
          sensorGroup: s.sensorGroup,
          sensorReadingType: s.sensorReadingType,
          numberOfSamples: s.numberOfSamples,
          lastXMinutes: s.lastXMinutes,
          analysisType: s.analysisType,
          comparator: s.comparator,
          threshold: s.threshold,
          logicOperator: s.logicOperator,
          order: s.order
        };
        sensorConditions.push(newSensorCondition);
      }
      originalEvent.sensorConditions = sensorConditions;
    }

    if (updatedEvent.timerConditions != undefined) {
      var timerConditions: TimerCondition[] = [];
      for (var index = 0; index < updatedEvent.timerConditions.length; index++) {
        var t = updatedEvent.timerConditions[index];
        const newTimerCondition: TimerCondition = {
          id: t.id,
          eventID: t.eventID,
          type: t.type,
          timingSingle: t.timingSingle,
          timingRepeat: t.timingRepeat,
          logicOperator: t.logicOperator,
          daysOfWeek: t.daysOfWeek,
          order: t.order
        };
        timerConditions.push(newTimerCondition);
      }
      originalEvent.timerConditions = timerConditions;
    }

    if (updatedEvent.eventActions != undefined) {
      var eventActions: EventAction[] = [];
      for (var index = 0; index < updatedEvent.eventActions.length; index++) {
        var a = updatedEvent.eventActions[index];
        const newEventAction: EventAction = {
          id: a.id,
          eventID: a.eventID,
          type: a.type,
          destination: a.destination,
          deviceSerial: a.deviceSerial,
          deviceName: a.deviceName,
          taskAction: a.taskAction,
          zone: a.zone,
          scheduleName: a.scheduleName,
          scheduleID: a.scheduleID,
          onError: a.onError,
          mediaResolution: a.mediaResolution,
          mediaDuration: a.mediaDuration,
          mediaDelay: a.mediaDelay,
          cameraId: a.cameraId,
          cameraName: a.cameraName,
          cameraModel: a.cameraModel,
          cameraPresets: a.cameraPresets,
          mediaActionOptions: a.mediaActionOptions,
          sourceZone: a.sourceZone,
          fillZone: a.fillZone,
          fillScheduleName: a.fillScheduleName,
          fillScheduleId: a.fillScheduleId,
          drainZone: a.drainZone,
          drainScheduleName: a.drainScheduleName,
          drainScheduleId: a.drainScheduleId,
          irrigationActionOptions: undefined,
          notificationSectionOptions: a.notificationSectionOptions
        };
        eventActions.push(newEventAction);
      }
      originalEvent.eventActions = eventActions;
    }
  }


  public convertDaysOfWeekString2BoolArray(daysOfWeek: string): boolean[] {
    const daysOfWeekBool: boolean[] = [ false, false, false, false, false, false, false ];
    for (let i = 0; i < daysOfWeek.length && i < 7; i++) {
      daysOfWeekBool[i] = daysOfWeek[i] === '1' ? true : false;
    }
    return daysOfWeekBool;
  }


  public convertDaysOfWeekBoolArray2String(daysOfWeek: boolean[]): string {
    let daysOfWeekString = '';

    for (let i = 0; i < daysOfWeek.length && i < 7; i++) {
      daysOfWeekString += daysOfWeek[i] === true ? '1' : '0';
    }
    return daysOfWeekString;
  }


  getTimerEventTypeAtIndex(index: number): TimerEventType {
    const enumString = (index >= 0) ? Object.values(TimerEventType)[index].toLowerCase() : '';

    switch (enumString) {
    case 'single':
      return TimerEventType.Single;
    case 'repeat':
      return TimerEventType.Repeat;
    default:
      return TimerEventType.Single;
    }
  }


  getActionTypeAtIndex(index: number): ActionType {
    const enumString = (index >= 0) ? Object.values(ActionType)[index].toLowerCase() : '';

    switch (enumString) {
    case 'email':
      return ActionType.Email;
    case 'url':
      return ActionType.Url;
    case 'task':
      return ActionType.Task;
    case 'irrigation':
      return ActionType.Irrigation;
    case 'appmessage':
      return ActionType.AppMessage;
    case 'captureimage':
      return ActionType.Camera;                   // Mapping OLD/unused type to 'Camera'
    case 'capturevideo':
      return ActionType.Camera;                   // Mapping OLD/unused type to 'Camera'
    case 'ptzcaptureimage':
      return ActionType.Camera;                   // Mapping OLD/unused type to 'Camera'
    case 'camera':
      return ActionType.Camera;
    case 'floodirrigation':
      return ActionType.FloodIrrigation;
    default:
      return ActionType.Email;
    }
  }


  getActionTypeName(actionType: ActionType): string {
    switch (actionType) {
    case ActionType.Email:
      return 'Email';
    case ActionType.Url:
      return 'Url';
    case ActionType.Task:
      return 'Task';
    case ActionType.Irrigation:
      return 'Irrigation';
    case ActionType.AppMessage:
      return 'AppMessage';
    case ActionType.CaptureImage:               // Mapping OLD/unused type to 'Camera'
      return 'Camera';
    case ActionType.CaptureVideo:               // Mapping OLD/unused type to 'Camera'
      return 'Camera';
    case ActionType.PTZCaptureImage:            // Mapping OLD/unused type to 'Camera'
      return 'Camera';
    case ActionType.Camera:
      return 'Camera';
    case ActionType.FloodIrrigation:
      return 'FloodIrrigation';
    default:
      return 'unknown';
    }
  }


  getComparatorAtIndex(index: number): Comparator {
    const enumString = (index >= 0) ? Object.values(Comparator)[index].toLowerCase() : '';

    switch (enumString) {
    case '<':
      return Comparator.lt;
    case '>':
      return Comparator.gt;
    case '<=':
      return Comparator.lte;
    case '>=':
      return Comparator.gte;
    case '=':
      return Comparator.eq;
    case '!=':
      return Comparator.neq;
    default:
      return Comparator.eq;
    }
  }

  getComparatorBySymbol(enumString: string): Comparator {
    switch (enumString) {
    case '<':
      return Comparator.lt;
    case '>':
      return Comparator.gt;
    case '<=':
      return Comparator.lte;
    case '>=':
      return Comparator.gte;
    case '=':
      return Comparator.eq;
    case '!=':
      return Comparator.neq;
    default:
      return Comparator.eq;
    }
  }

  getEventTypeAtIndex(index: number): EventType {
    const enumString = (index >= 0) ? Object.values(EventType)[index].toLowerCase() : '';
    switch (enumString) {
    case 'sensor based':
      return EventType.SensorBased;
    case 'timer based':
      return EventType.TimerBased;
    case 'model based':
      return EventType.ModelBased;
    case 'motion based':
      return EventType.MotionBased;
    default:
      return EventType.SensorBased;
    }
  }

  getAnalysisTypeAtIndex(index: number): AnalysisType {
    const enumString = (index >= 0) ? Object.values(AnalysisType)[index].toLowerCase() : '';
    switch (enumString) {
    case 'median':
      return AnalysisType.median;
    case 'average':
      return AnalysisType.average;
    case 'min':
      return AnalysisType.min;
    case 'max':
      return AnalysisType.max;
    case 'data rate':
      return AnalysisType.datarate;
    case '|meda-medb|':
      return AnalysisType.medAbsDiff;
    case '|avga-avgb|':
      return AnalysisType.avgAbsDiff;
    case '|mina-minb|':
      return AnalysisType.minAbsDiff;
    case '|maxa-maxb|':
      return AnalysisType.maxAbsDiff;
    case 'group count':
      return AnalysisType.groupcount;
    case 'single count':
      return AnalysisType.singlecount;
    case 'trimmed mean':
      return AnalysisType.trimmedMean;
    default:
      return AnalysisType.median;
    }
  }

  getAnalysisTypeBySymbol(enumString: string): AnalysisType {
    switch (enumString) {
    case 'median':
      return AnalysisType.median;
    case 'average':
      return AnalysisType.average;
    case 'min':
      return AnalysisType.min;
    case 'max':
      return AnalysisType.max;
    case 'data rate':
      return AnalysisType.datarate;
    case '|meda-medb|':
      return AnalysisType.medAbsDiff;
    case '|avga-avgb|':
      return AnalysisType.avgAbsDiff;
    case '|mina-minb|':
      return AnalysisType.minAbsDiff;
    case '|maxa-maxb|':
      return AnalysisType.maxAbsDiff;
    case 'group count':
      return AnalysisType.groupcount;
    case 'single count':
      return AnalysisType.singlecount;
    case 'trimmed mean':
      return AnalysisType.trimmedMean;
    default:
      return AnalysisType.median;
    }
  }

  getTimingIntervalAtIndex(index: number): TimingInterval {
    const enumString = (index >= 0) ? Object.values(TimingInterval)[index].toLowerCase() : '';
    switch (enumString) {
    case 'every half hour':
      return TimingInterval.EveryHalfHour;
    case 'every hour':
      return TimingInterval.EveryHour;
    case 'every 2 hours':
      return TimingInterval.EveryTwoHours;
    case 'every 4 hours':
      return TimingInterval.EveryFourHours;
    case 'every 8 hours':
      return TimingInterval.EveryEightHours;
    default:
      return TimingInterval.EveryTwoHours;
    }
  }

  getLogicOperatorAtIndex(index: number): LogicOperator {
    const enumString = (index >= 0) ? Object.values(LogicOperator)[index].toLowerCase() : '';
    switch (enumString) {
    case 'and':
      return LogicOperator.And;
    case 'or':
      return LogicOperator.Or;
    default:
      return LogicOperator.And;
    }
  }


  convertUTCDaysOfWeek2Local(orgStartTime: Date, newStartTime: string, orgDaysOfWeek: boolean[]): boolean[] {
    const utcHour = moment.utc(orgStartTime).format('HH').valueOf();
    const localHour = moment(newStartTime, 'HH:mm').format('HH').valueOf();

    if (utcHour < localHour) {
      const newDaysOfWeek: boolean[] = [];
      for (let i = 1; i < orgDaysOfWeek.length; i++) {
        newDaysOfWeek.push(orgDaysOfWeek[i]);
      }
      newDaysOfWeek.push(orgDaysOfWeek[0]);
      return newDaysOfWeek;
      //shift days of week to the left
    }
    return orgDaysOfWeek;
  }


  convertLocalDaysOfWeek2UTC(orgStartTime: string, newStartTime: Date, orgDaysOfWeek: string): string {
    const localHour = moment(orgStartTime, 'HH:mm').format('HH').valueOf();
    const utcHour = (moment(newStartTime).add(-(moment().utcOffset()), 'm')).format('HH').valueOf();

    if (utcHour < localHour) {
      const newDaysOfWeek = orgDaysOfWeek[6] + orgDaysOfWeek.slice(0, 6);
      return newDaysOfWeek;
      //shift days of week to the right
    }
    return orgDaysOfWeek;
  }


  convertUTCDaysOfWeek2LocalString(orgStartTime: Date, newStartTime: string, orgDaysOfWeek: string): string {
    const localHour = moment(orgStartTime).format('HH').valueOf();
    const utcHour = moment(newStartTime, 'HH:mm').format('HH').valueOf();

    if (utcHour > localHour) {
      let newDaysOfWeek = '';
      for (let i = 1; i < orgDaysOfWeek.length; i++) {
        newDaysOfWeek += orgDaysOfWeek[i];
      }
      newDaysOfWeek += orgDaysOfWeek[0];
      return newDaysOfWeek;
      //shift days of week to the left
    }
    return orgDaysOfWeek;
  }


  convertBoolArry2String(boolArray: boolean[]): string {
    let newString = '';
    if (boolArray == null) {
      return newString;
    }
    boolArray.forEach(b => {
      newString += b ? '1' : '0';
    });
    return newString;
  }

  time2UTC(timeString: string): Date {
    const newDate = moment(timeString, 'HH:mm').toDate();
    return newDate;
  }


  time2Local(timeDate: Date): string {
    const newDate = moment.utc(timeDate).local().format('HH:mm');
    return newDate;
  }

  sortTimerEventsByOrder(timerEvents: TimerCondition[]) {
    timerEvents.sort((a, b) => {
      if (b.order > a.order) {
        return -1;
      }
      if (b.order < a.order) {
        return 1;
      }
    });
  }

  sortMotionEventsByOrder(motionEvents: MotionCondition[]) {
    motionEvents.sort((a, b) => {
      if (b.order > a.order) {
        return -1;
      }
      if (b.order < a.order) {
        return 1;
      }
    });
  }

  sortSensorEventsByOrder(sensorEvents: SensorCondition[]) {
    sensorEvents.sort((a, b) => {
      if (b.order > a.order) {
        return -1;
      }
      if (b.order < a.order) {
        return 1;
      }
    });
  }

  sortConditionResultsByOrder(conditionResults: ConditionResult[]) {
    conditionResults.sort((a, b) => {
      if (b.order > a.order) {
        return -1;
      }
      if (b.order < a.order) {
        return 1;
      }
    });
  }

  sortSensorEventResultInfosByOrder(sensorEventResults: SensorEventResultInfo[]) {
    sensorEventResults.sort((a, b) => {
      if (b.order > a.order) {
        return -1;
      }
      if (b.order < a.order) {
        return 1;
      }
    });
  }

  sortTimerEventResultInfosByOrder(timerEventResults: TimerEventResultInfo[]) {
    timerEventResults.sort((a, b) => {
      if (b.order > a.order) {
        return -1;
      }
      if (b.order < a.order) {
        return 1;
      }
    });
  }


  getDeviceNameFromSerial(devices: DeviceInfo[], serial: string): string {
    const device = devices.find(d => d.serialNumber === serial);

    if (device === null || device === undefined || device.name === '') {
      return 'unknown';
    }
    return device.name;
  }


}
